import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from './api';
import { broadcastRevoked } from './sessionRevocation';
import {
  dccContinuationToken,
  safeDccContinuation,
  withDccContinuation,
} from './authContinuation';

export default function ResetPassword() {
  const [searchParams]              = useSearchParams();
  const token                       = searchParams.get('token') || '';

  // Completion mode (token present)
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [done, setDone]             = useState(false);

  // Request mode (no token)
  const [email, setEmail]           = useState('');
  const [requested, setRequested]   = useState(false);

  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const navigate                    = useNavigate();
  const redirect                    = searchParams.get('redirect');
  const dccContinuation             = safeDccContinuation(redirect);
  const continuationToken           = dccContinuationToken(dccContinuation);

  const handleRequest = async e => {
    e.preventDefault();
    setError('');
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      await API.post('/v1/auth/forgot-password', { email, continuation: continuationToken || undefined });
      // Backend returns a neutral response regardless of whether the account exists.
      // Show that same neutral confirmation without revealing account existence.
      setRequested(true);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'Something went wrong. Please try again in a moment.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async e => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('No reset token found. Please use the link from your email.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await API.post('/v1/auth/reset-password', { token, password });
      setDone(true);
      broadcastRevoked({ reason: 'password-reset', scope: 'all' });
    } catch (err) {
      setError(
        err.response?.data?.error ||
        'This reset link is invalid or has expired. Please request a new one.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Completion success
  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>Visinvoke</div>
          <div style={styles.iconSuccess}>✓</div>
          <h2 style={styles.title}>Password Reset</h2>
          <p style={styles.body}>Your password has been updated. You can now sign in with your new password.</p>
          <button style={styles.btn} onClick={() => navigate(withDccContinuation('/login', dccContinuation))}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Request submitted - neutral confirmation (does not reveal whether the account exists)
  if (requested) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>Visinvoke</div>
          <div style={styles.iconSuccess}>✓</div>
          <h2 style={styles.title}>Check Your Email</h2>
          <p style={styles.body}>If an account exists with that email, a password reset link has been sent. The link is valid for 2 hours.</p>
          <button style={styles.btn} onClick={() => navigate(withDccContinuation('/login', dccContinuation))}>
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Request mode - no token in the URL: collect the email address
  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>Visinvoke</div>
          <h2 style={styles.title}>Reset Password</h2>
          <p style={styles.body}>Enter your account email and we'll send you a link to reset your password.</p>
          {error && <div style={styles.error}>{error}</div>}
          <form onSubmit={handleRequest}>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                style={styles.input}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
          <p style={styles.back}>
            <span style={styles.backLink} onClick={() => navigate(withDccContinuation('/login', dccContinuation))}>
              Back to Sign In
            </span>
          </p>
        </div>
      </div>
    );
  }

  // Completion mode - token present: set the new password
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>Visinvoke</div>
        <h2 style={styles.title}>Set New Password</h2>
        {error && <div style={styles.error}>{error}</div>}
        <form onSubmit={handleReset}>
          <div style={styles.field}>
            <label style={styles.label}>New Password</label>
            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm New Password</label>
            <input
              style={styles.input}
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              required
            />
          </div>
          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Updating...' : 'Set New Password'}
          </button>
        </form>
        <p style={styles.back}>
          <span style={styles.backLink} onClick={() => navigate(withDccContinuation('/login', dccContinuation))}>
            Back to Sign In
          </span>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page:        { minHeight: '100vh', background: 'linear-gradient(135deg, #334155, #881337)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card:        { background: '#fff', borderRadius: 12, padding: '40px 36px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  logo:        { fontWeight: 900, fontSize: 22, color: '#8b5cf6', marginBottom: 8 },
  title:       { margin: '0 0 24px', fontSize: 20, fontWeight: 700, color: '#1a1a1a' },
  error:       { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  field:       { marginBottom: 16 },
  label:       { display: 'block', fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 },
  input:       { width: '100%', border: '2px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' },
  btn:         { width: '100%', background: 'linear-gradient(135deg, #8b5cf6, #a855f7)', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 8 },
  iconSuccess: { fontSize: 40, color: '#16a34a', marginBottom: 12, textAlign: 'center' },
  body:        { fontSize: 14, color: '#555', lineHeight: 1.6, margin: '0 0 24px' },
  back:        { textAlign: 'center', marginTop: 20, fontSize: 13, color: '#666' },
  backLink:    { color: '#8b5cf6', fontWeight: 600, cursor: 'pointer' },
};
